import { gql, useQuery as useClientQuery } from "@apollo/client";
import { REACT_APP_MULTICALL } from "constants/NetworkChainId";
import { BigNumber, Contract, ethers } from "ethers";
import { useWallet } from "hooks";
import { get, groupBy } from "lodash";
import { useMemo } from "react";
import { useQuery } from "react-query";
import {
  decodeResultMulticallZkSync,
  formatMulticallResults,
} from "utils/contract/decodeResultMulticall";
import { MultiCallZkSyncABI } from "utils/ethereum/abi";
import { getPrimaryIndexToken } from "utils/ethereum/contracts";
import Eth from "web3-eth";
import { getLogoBySymbolAndName, getSpecialTokenSymbol } from "utils/utils";
import { getLvrFromResult } from "./helper/getDataContract";

const GET_PRJ_TOKEN = gql`
  query ExampleQuery($account: String!) {
    projectTokens {
      name
      symbol
      address
      underlyingTokens {
        id
        name
        symbol
        address
        linksNumber
      }
    }
    lendingTokens {
      name
      symbol
      address
    }
    borrowLogs(where: { userAddress: $account }) {
      id
      amount
      asset
      prjToken
      type
      date
      userAddress
      prjTokenAddress
    }
  }
`;

export const useGetTokens = () => {
  const { account, chainId } = useWallet();

  const { data, refetch } = useClientQuery(GET_PRJ_TOKEN, {
    variables: { account },
  });

  return {
    projectTokenList: get(data, ["projectTokens"], []).map((o) => ({
      ...o,
      type: "projectToken",
      logo:
        getLogoBySymbolAndName(o.symbol, o.name, o?.address) ||
        "https://s2.coinmarketcap.com/static/img/coins/64x64/3267.png",
      underlyingTokens: o.underlyingTokens,
      symbol: getSpecialTokenSymbol(o.address, chainId) || o.symbol,
    })),
    updateDataGraph: refetch,
    borrowLogs: groupBy(get(data, ["borrowLogs"]), "prjTokenAddress"),
    availableBorrowTokens: get(data, ["lendingTokens"], []).map((o) => ({
      ...o,
      type: "lendingToken",
      logo: getLogoBySymbolAndName(o.symbol, o.name, o?.address),
      symbol: getSpecialTokenSymbol(o.address, chainId) || o.symbol,
    })),
  };
};

const GET_PRJ_TOKEN_WITHOUT_ACCOUNT = gql`
  query ExampleQuery {
    projectTokens {
      name
      symbol
      address
      underlyingTokens {
        id
        name
        symbol
        address
        linksNumber
      }
    }
    lendingTokens {
      name
      symbol
      address
    }
  }
`;

function getRequestMulticall(PITToken, projectTokens, provider) {
  if (!PITToken.address) return null;

  const listRequestToPITContract = {
    contractAddress: PITToken.address,
    abi: PITToken.abi,
    reference: "PITContract",
    calls: [],
  };

  const PITContract = new Contract(PITToken.address, PITToken.abi, provider);
  projectTokens.forEach((token) => {
    listRequestToPITContract.calls.push({
      target: PITToken.address,
      callData: PITContract.interface.encodeFunctionData("projectTokenInfo", [token.address]),
      reference: `${token.address}`,
      methodName: "projectTokenInfo",
      contract: PITContract,
      contractName: "PITContract",
      methodParameters: [token.address],
      value: ethers.BigNumber.from(0),
    });
  });

  return listRequestToPITContract.calls;
}

function handleDataFromMulticall(result, projectTokenList) {
  const PITContract = get(result, ["PITContract"], []);
  const projectTokens = PITContract.map((token) => {
    const prjTokenAddress = get(token, ["methodParameters", 0], "");
    const tokenInfo = projectTokenList.find((o) => o.address === prjTokenAddress);
    const lvr = getLvrFromResult(result, prjTokenAddress);

    return {
      ...tokenInfo,
      lvr,
      logo: getLogoBySymbolAndName(tokenInfo.symbol, tokenInfo.name, tokenInfo?.address),
    };
  });

  return projectTokens;
}

export const useGetTokensWithoutAccount = () => {
  const { data } = useClientQuery(GET_PRJ_TOKEN_WITHOUT_ACCOUNT);

  const projectTokens = useMemo(() => get(data, "projectTokens", []), [data]);
  const lendingTokens = useMemo(() => get(data, "lendingTokens", []), [data]);

  const metamaskProvider =
    window.ethereum?.providers?.find((provider) => provider.isMetaMask) || window.ethereum;

  const eth = new Eth(metamaskProvider);
  const listTokenWithoutAccount = useQuery(
    ["get-token-without-account", projectTokens, lendingTokens],
    async () => {
      const getChainId = await eth.getChainId();
      const provider = new ethers.providers.Web3Provider(metamaskProvider);
      const chainId = ethers.utils.hexValue(getChainId);

      const getMultiCallContract = () => {
        const multiCallAddress = REACT_APP_MULTICALL[chainId];
        if (!multiCallAddress) return null;

        const multiCallSMC = new Contract(multiCallAddress, MultiCallZkSyncABI, provider);

        return multiCallSMC;
      };

      const multiCallContract = getMultiCallContract();

      const PITToken = getPrimaryIndexToken(chainId);

      const listRequest = getRequestMulticall(PITToken, projectTokens, provider);

      const valueOfRequest = listRequest?.map((r) => ("value" in r ? r.value : BigNumber.from(0)));
      const totalValue = valueOfRequest?.reduce((pre, cur) => pre.add(cur), BigNumber.from(0));

      const resultMulticall = await multiCallContract.callStatic.aggregate3Value(listRequest, {
        value: totalValue,
      });

      const returnData = formatMulticallResults(resultMulticall);

      const results = decodeResultMulticallZkSync(listRequest, returnData);

      const listToken = handleDataFromMulticall(results, projectTokens);

      return {
        projectTokens: listToken.map((token) => ({
          ...token,
          allowance: false,
          balance: "0.0",
          healthFactor: 0,
          price: 0,
          isLeverage: false,
        })),
        lendingTokens: lendingTokens.map((token) => ({
          ...token,
          logo: getLogoBySymbolAndName(token.symbol, token.name, token?.address),
          cash: "0.0",
          balanceOf: "0.0",
          priceInfo: {
            price: "0",
          },
          balanceInUsd: "0",
          balanceOfUnderlyingView: "0",
          apy: "0",
        })),
      };
    },
    {
      enabled: !!lendingTokens?.length && !!projectTokens?.length && !!metamaskProvider,
    }
  );
  return {
    data: listTokenWithoutAccount.data,
    error: listTokenWithoutAccount.isError,
    isLoading: listTokenWithoutAccount.isLoading,
  };
};
